const posts = [
  {
    id: 1,
    title: "Being a DevOps Engineer",
    content: `
Being a DevOps engineer is an exciting and rewarding career path in the technology industry. DevOps is a set of practices that aim to merge the development and operations functions of software development to improve the speed and quality of software delivery.

As a DevOps engineer, you play a critical role in ensuring that software development and deployment are seamless, efficient, and reliable. Here are some key responsibilities of a DevOps engineer:

1. Collaborate with development teams: DevOps engineers work closely with software developers to understand their needs, requirements, and constraints, and provide them with the necessary tools and infrastructure to develop, test, and deploy software.

2. Automate processes: One of the primary objectives of DevOps is to automate as many processes as possible. DevOps engineers use tools like Ansible, Chef, Puppet, and Jenkins to automate tasks like testing, building, and deploying software.

3. Monitor and maintain systems: DevOps engineers are responsible for ensuring that the software systems are up and running at all times. They monitor the system's performance and identify potential issues before they become problems.

4. Continuously improve: DevOps engineers are constantly looking for ways to improve the software development and deployment process. They analyze data and metrics to identify areas for improvement, and then implement changes to streamline and optimize the process.

To become a successful DevOps engineer, you will need to have a solid understanding of software development, infrastructure management, and automation tools. You should also be familiar with agile methodologies and have excellent communication and collaboration skills.

In conclusion, being a DevOps engineer can be a challenging but rewarding career path in the technology industry. With the right skills and mindset, you can help organizations improve their software development and deployment processes, and make a significant impact on their success.
    `,
    date: "2024-08-11",
    category: "DevOps",
    featureImage: "/images/devops.png",
    featured: false,
    description: "This post contains the ups and downs of a DevOps Engineer"
  },
  {
    id: 2,
    title: "Basics of Docker",
    content: `
If you are a developer, then you must have heard this statement “This works on my system”. To solve this issue and to ease up your deployment we use docker.

## 💭 What is Docker? 
Docker is an open-source platform that makes software deployment stable and less prone to change. It provides an excellent operating environment that allows you to deploy software with consistency and efficiency across different environments and systems.

Let’s take an example, Suppose you have written code for an application in Java. Your code might be dependent upon certain libraries like OpenJDK and that too on some particular versions. So when you ship this application, your end user or your customer must have the same libraries to run your application smoothly. 
Here comes Docker to help you with this problem. 

So instead of shipping just your application, you can ship what we called a “Docker Image”. Your end user can still use your application with no worries about the libraries they have. Because this so-called docker image, you are shipping, contains everything required to run the application including the libraries like OpenJDK. 

And another advantage is with the deployment. that end user just needs to run a single docker command to run your application. They even don’t have to install your application.

You can watch the video below for the demo:

![youtube](qFUbaoPqXpk)


## Key concepts: 
Now let’s see some of the terms used in docker space.

### Docker Image 
If you have ever installed an operating then you can say that the docker image is like an iso file. It contains all the environmental libraries and applications. 
    
### Container 
A container is a running image. Just like we run a machine after installing the OS. You can run any number of containers from a single image. You can also access the content from inside a running container. If there is an application listening to a port inside the container, then you can map it to listen to your host machine. 

### Host Machine 
So the system or machine in which we set up docker is called the host machine.  Then in the host machine, we run what we called as docker service. Docker service helps us to run the docker containers. And each container has their own operating system and environment setup. These containers are created from Docker Images. 

### Dockerfile
A Dockerfile is a text file that contains a list of commands that are used to automate the creation of a Docker image. It contains all the commands necessary to build a Docker image from scratch, including instructions for installing software, setting environment variables, and exposing ports. Once the Dockerfile is complete, it can be used to create a Docker image using the \`docker build\` command.

### Docker vs Virtual Machine
If I compare docker with a virtual machine. Docker is lighter and more efficient than virtual machines, as it shares the host operating system kernel and only virtualizes the application environment. In contrast, virtual machines virtualize the entire operating system, which can be much heavier and slower.

## Let's try out some docker commands
To pull the image
\`\`\`bash
docker pull nginx
\`\`\`
After pulling the image, you can check the list of images present locally in your host machine.
\`\`\`bash
docker images
\`\`\`
Use the 'docker run' command to create a docker container from the image
\`\`\`bash
docker run --name some-nginx -d -p 8080:80 nginx
\`\`\`
'-p' flag is to map host machine port to the container port(host:container)

'--name' flag is to assign the name of the container

'-d' to run the container in the background

Now, from the browser, open the link 'http://localhost:8080'

Check the list of running containers
\`\`\`bash
docker ps
\`\`\`
If the container is stopped then use the '-a' flag to list all containers.

To check the logs of the container
\`\`\`bash
#Either use container name or container id
docker logs <container_name/container_id>
\`\`\`
To stop and remove the container use:
\`\`\`bash
#Stop Container
docker stop container_name

#Remove the container
docker rm container_name
\`\`\`
To remove the image:
\`\`\`bash
docker rmi nginx
\`\`\`
    
## Conclusion: 
Docker is a powerful tool to build and manage software and its dependencies. It also provides ease of deployment and scalability for your applications. It is a suggestion to any developer or DevOps engineer to have a basic understanding of Docker.
#### Important links
- [Docker Download](https://docs.docker.com/get-docker/)
- [Docker Hub](https://hub.docker.com/search?q=&type=image)
- [Docker Documentation](https://docs.docker.com/)
    `,
    date: "2023-08-20",
    category: "Frontend",
    featureImage: "/images/dockerfile.jpg",
    featured: false,
    description: "A beginner's guide to understanding Docker and its benefits."
  },
  {
    id: 3,
    title: "Build your own containers | Dockerfile",
    content: `
Dockerfile is basically a configuration file that is use to create docker image. It contains list of commands that will run in order to assemble the image.

## 💭 Understand by an example: 

Suppose you have a application and you want to containerize it(installing it in docker container) and then use it.

So what is the actual requirement? Basically you need a container which has the dependent libraries.
Then you can copy the source code into the container. Install the application if required. And then run the application. 
All of these steps can be done via a dockerfile. And in the end you will get a docker image. And when you run this image your application will be started.

## Let's try it out:
If I take an example of python application. Here is my simple python application which is creating a server to show the list of files in the directory:
\`\`\`python
import http.server
import socketserver

PORT = 8000

handler = http.server.SimpleHTTPRequestHandler

with socketserver.TCPServer("", PORT), handler) as httpd:
    print("Server started at localhost:" + str(PORT))
    httpd.serve_forever()
\`\`\`  
Now if I check the dependencies for this application. I can say it is mainly dependent on python. 
So to containerize the application I need to create a docker image that contains python and my application.

So instead of creating the image from scratch, I can just use the existing Docker image of python. It is available in docker hub and I can built on top of it. 
So here is the example dockerfile.
\`\`\`Dockerfile
# Setting up base image
FROM python:3.10-alpine

# Copy source code into the image 
COPY . /app/

# Expose the port where the application is listening to
EXPOSE 8000

# Set the working directory to execute further commands
WORKDIR /app

# Running the application
CMD [ "python3", "server.py" ]
\`\`\`
In the above file, firstly, I am using the **python:3.10-alpine** as my base image. And now since my major dependency is fullfiled, I just copied the source code ( all the files present in the current directory ) and exposed the port where the application is listening to. This allows my application to take requests from the host machine where the docker daemon is running.

Next, I am setting the working directory. So any command I will run will be executed in that directory. 

Then I am running the command to start the application. Save the file with the name *Dockerfile*.

All these statements are adding layers to the initial python image. The more layers you have, the bigger is the size of the image that it will create. 

Now to build the image use the command:
\`\`\`bash
docker build -t myapp:1.0 -f Dockerfile .
\`\`\`
-t flag is to tag the image with name:version
-f flag is used to refer your dockerfile. 
'.' is to set the context of the docker build to the current folder

And you can run the image to create a container:
\`\`\`bash
docker run -p 9000:8000 myapp:1.0
\`\`\`
-p flag to map host port to the container port


Now we have containerize the application. You can watch the video below for the demo:

![youtube](LBCNbGB_SyE)
    
## Conclusion: 
Dockerfile is an essential part of Docker. This helps us to understand how docker images are built and how we can create our own images. Try out the demo showed above and always try to follow the best practices mentioned in the documentation.
#### Important links

- [DockerFile Reference](https://docs.docker.com/engine/reference/builder/)
- [Docker Hub](https://hub.docker.com/search?q=&type=image)
- [Docker Documentation](https://docs.docker.com/)
    `,
    date: "2023-09-18",
    category: "Docker",
    featureImage: "/images/docker.jpg",
    featured: false,
    description: "This post is about dockerfile and how to build your own containers."
  },
  {
    id: 4,
    title: "Communication between Containers using Docker Networking",
    content: `
Using docker we have separate out the application. And we have kept it’s environment limited to the container. But every service or application needs to connect with services. For this we have docker networking.

## 💭 What is Docker Networking?

Docker Networking is a way of managing how containers communicate with each other and with the outside world. 

By default, Docker containers can communicate with each other on the same host, but not with containers on other hosts or with external networks. Docker Networking enables us to connect containers with each other and with external networks.

## Types of Docker Networking:

There are 7 types of Docker Networking:

\

### Bridge Networking
In Docker, a bridge network is the default network driver that enables communication between containers on the same host. When a container is started with the default bridge network, it gets a unique IP address, and it can communicate with other containers on the same network using that IP address. In this video, we will dive into bridge networking in Docker.
    
Check the list of existing networks
\`\`\`bash
$ docker network ls
\`\`\`

Create some random containers to test the network.
\`\`\`bash
$ docker run -d --name container1 nginx
$ docker run -d --name container2 nginx
\`\`\`
Inspect the default bridge network to see the connected containers
\`\`\`bash
$ docker network inspect default
\`\`\`
Here you find that the default bridge network is connected to the containers that we have created. You can also try to ping one container from the terminal of another container.
    
### User Defined bridge networking
As the name states that you can define your own bridge network. This will help you in creating a separate network for a set of containers. 
    
To create a bridge network, we can use the \`docker network create\` command with the \`bridge\` driver. For example, let's create a network named \`my_network\`:
\`\`\`bash
$ docker network create --driver bridge my-network
\`\`\`
Once the network is created, we can connect containers to it by specifying the network name when starting the container. For example, let's start two containers and connect them to **\`my_network\`**:
\`\`\`bash
$ docker run -d --name container1 --network my-network nginx
$ docker run -d --name container2 --network my-network nginx
\`\`\`
Now, \`container1\` and \`container2\` are connected to the \`my-network\` bridge network, and they can communicate with each other using their unique IP addresses.
    
We can inspect the \`my-network\` bridge network to see the connected containers and their IP addresses:
\`\`\`bash
$ docker network inspect my_network
\`\`\`
This command will output a JSON object that includes information about the network, including the containers connected to it and their IP addresses. 
    
### Host Networking: 
    
Host networking mode allows a container to use the host's network stack and bypass Docker's network stack. This can provide better network performance for the container, but it also means that the container shares the same network namespace as the host. Let's see how to setup the network

To start a container in host networking mode, we can use the \`--network host\` option when running the \`docker run\` command. For example, let's start a container in host networking mode:
\`\`\`bash
$ docker run -d --name container1 --network host nginx
\`\`\`
This command will start a container named \`container1\` in host networking mode, using the host's network stack.

We can inspect the network namespace of the container to see that it is using the host's network stack. To do this, we can use the **nsenter** command to enter the container's network namespace:
\`\`\`bash
# Remove "\\" from the command below
$ docker inspect -f "{{.State.Pid}}" container1
$ sudo nsenter -t <container-pid> -n ip addr
\`\`\`
This command will show us the IP address assigned to the container and confirm that it is using the host's network stack.
    
#### Pros and Cons of Host Networking
    
The main advantage of host networking mode is improved network performance since the container is using the host's network stack directly. However, this also means that the container shares the same network namespace as the host, which can be a security risk. Additionally, using host networking mode can limit the portability of the container since it relies on the specific network configuration of the host.
    
### Overlay Networking: 
    
Overlay networking is a network driver that enables communication between containers running on different hosts. This is useful for distributed applications that span multiple hosts or clusters. Let's see how you can configure overlay networking.
    
To create an overlay network, we can use the \`docker network create\` command with the \`overlay\` driver. For example, let's create an overlay network named \`my_overlay_network\`:
\`\`\`bash
$ docker network create --driver overlay my_overlay_network
\`\`\`
Once the network is created, we can connect containers to it by specifying the network name when starting the container. For example, let's start two containers on different hosts and connect them to **\`my_overlay_network\`**:
\`\`\`bash
[Host 1]$ docker run -d --name container1 --network my_overlay_network nginx
[Host 2]$ docker run -d --name container2 --network my_overlay_network nginx
\`\`\`
Now, \`container1\` on \`Host 1\` and \`container2\` on \`Host 2\` are connected to the \`my_overlay_network\` overlay network, and they can communicate with each other using their unique IP addresses.
    
We can inspect the \`my_overlay_network\` overlay network to see the connected containers and their IP addresses:
\`\`\`bash
[Host 1]$ docker network inspect my_overlay_network
\`\`\`
This command will output a JSON object that includes information about the network, including the containers connected to it and their IP addresses.
    
#### Pros and Cons of Overlay Networking
    
The main advantage of overlay networking is that it enables communication between containers running on different hosts, which is useful for distributed applications. However, overlay networking can be slower than other network drivers since it involves additional network hops. Additionally, overlay networking requires more configuration and setup than other network drivers.
    
### Macvlan Networking
    
Macvlan is a network driver that allows containers to have their own MAC addresses on the network. This is useful for applications that require direct access to the network hardware or need to appear as separate devices on the network. Let's look at the example below.
        
To create a Macvlan network, we can use the \`docker network create\` command with the \`macvlan\` driver. For example, let's create a Macvlan network named \`my_macvlan_network\`:
\`\`\`bash
$ docker network create -d macvlan \\
  --subnet=<subnet> \\
  --gateway=<gateway> \\
  -o parent=<physical-interface> \\
  my_macvlan_network

# subnet is the subnet mask you want to provide for your container
# gateway is the ip address of your router
$ ip route show 0.0.0.0/0 dev eth0 | cut -d' ' -f3

# for parent try the command
$ ip link show
\`\`\`
In this command, we specify the network details such as the subnet, gateway, and parent physical interface.

Once the network is created, we can connect containers to it by specifying the network name and the \`macvlan\` driver when starting the container. For example, let's start a container and connect it to \`my_macvlan_network\`:
\`\`\`bash
$ docker run -d --name container1 --network my_macvlan_network --network-driver macvlan nginx
\`\`\`
We can inspect the \`my_macvlan_network\` Macvlan network to see the connected containers and their MAC addresses:
\`\`\`bash
$ docker network inspect my_macvlan_network
\`\`\`
#### Pros and Cons of Macvlan Networking
    
The main advantage of Macvlan networking is that it allows containers to have their own MAC addresses on the network, which is useful for applications that require direct access to the network hardware or need to appear as separate devices on the network. However, Macvlan networking can be more complex to set up and configure than other network drivers. Additionally, using Macvlan networking can cause security concerns since it is possible for containers to spoof MAC addresses and potentially disrupt network traffic.
    
### IPVlan Networking
    
IPVlan is a network driver that allows containers to have their own IP addresses on the network. This is useful for applications that require direct access to the network hardware or need to appear as separate devices on the network. Let's see a simple example. 
    
To create an IPVlan network, we can use the \`docker network create\` command with the \`ipvlan\` driver. For example, let's create an IPVlan network named \`my_ipvlan_network\`:
\`\`\`bash
$ docker network create -d ipvlan \\
  --subnet=<subnet> \\
  --gateway=<gateway> \\
  -o parent=<physical-interface> \\
  my_ipvlan_network
\`\`\`
In this command, we specify the network details such as the subnet, gateway, and parent physical interface.

Once the network is created, we can connect containers to it by specifying the network name and the \`ipvlan\` driver when starting the container. For example, let's start a container and connect it to \`my_ipvlan_network\`:
\`\`\`bash
$ docker run -d --name container1 --network my_ipvlan_network --network-driver ipvlan nginx
\`\`\`
We can inspect the \`my_ipvlan_network\` IPVlan network to see the connected containers and their IP addresses:
\`\`\`bash
$ docker network inspect my_ipvlan_network
\`\`\`
This command will output a JSON object that includes information about the network, including the containers connected to it and their IP addresses.
    
#### Pros and Cons of IPvlan Networking
    
The main advantage of IPVlan networking is that it allows containers to have their own IP addresses on the network, which is useful for applications that require direct access to the network hardware or need to appear as separate devices on the network. However, IPVlan networking can be more complex to set up and configure than other network drivers. Additionally, using IPVlan networking can cause security concerns since it is possible for containers to disrupt network traffic.
    
### None networking
    
The \`none\` network driver is a special network driver that disables networking for a container. This can be useful for running containers that do not require any network access, such as containers that perform background tasks or run batch jobs. Let's look at the example below.
    
To create a container with the \`none\` network driver, we can use the --network none option when starting the container. For example, let's start a container with the \`none\` network driver:
\`\`\`bash
$ docker run -d --name container1 --network none nginx
\`\`\`
We can inspect the \`container1\` container to see that it has no network interfaces:
\`\`\`bash
$ docker inspect container1
\`\`\`
This command will output a JSON object that includes information about the container, including its network settings.

![youtube](42H28M9rQq8)
    
## Conclusion
I can understand if all this overwhemling. But from a practical standpoint if you know the basics of docker then you can jump towards learning the orchestration tools like kubernetes. I might be wrong but till now I have just used user defined bridge network in my practical experience. So you don't need to keep all this in mind and can always get back to this video or the documents.

#### Important links

- [DockerFile Reference](https://docs.docker.com/engine/reference/builder/)
- [Docker Hub](https://hub.docker.com/search?q=&type=image)
- [Docker Documentation](https://docs.docker.com/)

`,
    date: "2023-10-12",
    category: "Docker",
    featureImage: "/images/networking.png",
    featured: false,
    description: "This post contains the basics of docker networking and different types of networking drivers."
  },
  {
    id: 5,
    title: "Work with multiple services using Docker Compose",
    content: `
Setting up multiple containers using docker run command can be a tedious job. And it become more tough when you are in a dev environment and you have to build the image each time with source code changes. 

## 💭 Introduction to Docker Compose
Docker Compose is a tool that allows you to define and run multi-container Docker applications. It uses a YAML file to define the services, networks, and volumes required for your application, making it easy to manage complex, multi-container applications.

Docker Compose makes it easy to start and stop your multi-container application using a single command. It also allows you to scale your application by increasing or decreasing the number of instances of a particular service.

Docker Compose is particularly useful for development and testing environments, as it allows developers to easily manage and test complex, multi-container applications on their local machines. It can also be used in production environments to manage complex, multi-container applications across different hosts and environments.

Overall, Docker Compose is a powerful tool that simplifies the management of multi-container Docker applications, making it easier to build, test, and deploy complex applications.

## What is Docker Compose used for?
    
With Docker Compose, you can define all the services required for your application in a single file, making it easy to manage and deploy your application across different environments. Docker Compose allows you to define multiple containers, each with their own configuration options such as environment variables, ports, volumes, and network connections.
    
## How to install Docker Compose
    
Docker desktop already have docker compose but for linux you can use https://docs.docker.com/compose/install/
    
## Crafting Your Docker Compose Adventure

So, you've decided to embark on a Docker Compose journey, and we're here to make the ride smooth and enjoyable! Let's dive into creating a Docker Compose file together.

\`\`\`yaml
version: '3'

services:
  db:
    image: mysql:latest
    restart: always
    environment:
      MYSQL_ROOT_PASSWORD: example
    networks:
      - backend
  server:
    build:
      context: .
      dockerfile: Dockerfile.server
    ports:
      - "80:80"
    depends_on:
      - db
    environment:
      DB_HOST: db
      DB_USERNAME: root
      DB_PASSWORD: example
    networks:
      - backend

networks:
  backend:
\`\`\`

### Let's Break It Down
In this adventure, we're creating two service companions: db and server.

#### The DB Service:
- Our trusty db service conjures a MySQL container using the latest image.
- It's designed to restart automatically, ensuring it never misses a beat.
- We've set the MYSQL_ROOT_PASSWORD to "example" for security.
- This service joins the "backend" network, our behind-the-scenes bridge.

#### The Server Service:
- Meet our dynamic server service! Every time you run this Docker Compose tale, it builds a container from the specified context and Dockerfile (Dockerfile.server).
- The container's port 80 is mapped to the host's port 80, making sure it's easily accessible.
- The depends_on spell ensures that the db service is up and ready before our server takes the stage.
- Environment variables like DB_HOST, DB_USERNAME, and DB_PASSWORD are set for smooth communication with the db.
- Just like the db, our server dances on the "backend" network.

#### Network Magic:

- The "backend" network connects our services. It's like the secret passage allowing the server to chat with the db.
- When the server wants to talk to the db, it says "db.backend." Magic, right?

This is our Dockerfile.server

\`\`\`Dockerfile
FROM python:3.10-alpine

WORKDIR /app

COPY requirements.txt .

RUN pip install --no-cache-dir -r requirements.txt

COPY . .

CMD ["python", "server.py"]
\`\`\`
This file conjures up a Python environment in the server container and runs our server.py script.
    
## Running a Docker Compose file
To unleash this spectacle, run:

\`\`\`bash
docker-compose up
\`\`\`
But hey, if you just want to prepare the stage (build the image), use:

\`\`\`bash
docker-compose build server
\`\`\`
    
## Most used Docker Compose commands and options
Here are some of the most commonly used Docker Compose commands and options:

### Commands:

1. **\`docker-compose up\`**:
   - **Usage**: \`docker-compose up [options] [SERVICE...]\`
   - **Description**: Builds, (re)creates, starts, and attaches to containers for a service.

2. **\`docker-compose down\`**:
   - **Usage**: \`docker-compose down [options]\`
   - **Description**: Stops and removes containers, networks, volumes, and images created by \`up\`.

3. **\`docker-compose ps\`**:
   - **Usage**: \`docker-compose ps [options] [SERVICE...]\`
   - **Description**: Lists containers.

4. **\`docker-compose logs\`**:
   - **Usage**: \`docker-compose logs [options] [SERVICE...]\`
   - **Description**: View output from containers.

5. **\`docker-compose exec\`**:
   - **Usage**: \`docker-compose exec [options] SERVICE COMMAND [ARGS...]\`
   - **Description**: Run a command in a running service container.

6. **\`docker-compose build\`**:
   - **Usage**: \`docker-compose build [options] [SERVICE...]\`
   - **Description**: Builds or rebuilds services.

7. **\`docker-compose pull\`**:
   - **Usage**: \`docker-compose pull [options] [SERVICE...]\`
   - **Description**: Pulls images for services.

8. **\`docker-compose restart\`**:
   - **Usage**: \`docker-compose restart [options] [SERVICE...]\`
   - **Description**: Restarts services.

### Options:

1. **\`-f\`, \`--file FILE\`**:
   - **Description**: Specify an alternate compose file. Default is \`docker-compose.yml\`.

2. **\`-p\`, \`--project-name NAME\`**:
   - **Description**: Specify an alternate project name.

3. **\`-d\`, \`--detach\`**:
   - **Description**: Run containers in the background.

4. **\`-v\`, \`--volumes\`**:
   - **Description**: Mount host volumes into services.

5. **\`--build\`**:
   - **Description**: Build images before starting containers.

6. **\`--force-recreate\`**:
   - **Description**: Recreate containers even if their configuration and image haven't changed.

7. **\`--no-deps\`**:
   - **Description**: Don't start linked services.

8. **\`-e\`, \`--env\`**:
   - **Description**: Set environment variables.

9. **\`--scale SERVICE=NUM\`**:
   - **Description**: Set the number of containers to run for a service.

10. **\`--remove-orphans\`**:
    - **Description**: Remove containers for services not defined in the Compose file.

11. **\`--force-recreate\`**:
    - **Description**: Recreate containers even if their configuration and image haven't changed.

12. **\`-h\`, \`--help\`**:
    - **Description**: Display help.

These commands and options provide a solid foundation for working with Docker Compose, allowing you to manage, scale, and control your multi-container applications efficiently. Always refer to the [official documentation](https://docs.docker.com/compose/) for the most up-to-date information and additional options.

### Conclusion

Our journey merely scratched the surface of Docker Compose’s magic. From orchestrating multi-container symphonies to ensuring seamless communication, Docker Compose proves a valuable ally in the realm of containerization.

As you continue your Docker Compose adventure, explore its nuances, experiment with different services, and craft your own orchestration symphony. May your containers be ever resilient, your networks ever connected, and your deployments ever enchanting.

#### Important Links:
- [Docker Compose](https://docs.docker.com/compose/)
- [Installing Docker Compose](https://docs.docker.com/compose/install/)
- [Sample Apps](https://docs.docker.com/compose/samples-for-compose/)

`,
    date: "2023-11-29",
    category: "Docker",
    featureImage: "/images/container-db.png",
    featured: false,
    description: "This post contains the basics about Docker Compose."
  },
  {
    id: 6,
    title: "Basics of GitHub Actions",
    content: `
As a developer you have a lot of tasks to automate for example to trigger automated tests for a pull request or to run a cron job for the source code. With Github Actions you can run workflows that can perform these tasks as set/triggered. It also helps you in creating automation pipelines for continuous integration and continuous delivery(CI/CD). 

It is a useful tool for developers to automate repetative tasks, for Quality Engineers to test the product, for Platform Engineers to create the infrastructure deployment pipelines and for SRE (Site Reliability Engineers) to deploy in production. 
Today we will go through basics of github actions and create a workflow. 

## 💭 what is a workflow in github actions?

![Workflow Diagram](/images/workflow.png)

A workflow is basically a configuration yaml file that contains the flow of the automation that you want to set. It is majorly divided into three parts:


- **Trigger event of Workflow**: Here you can set the event or time at which you want to trigger the workflow. For example, you can trigger the steps in the workflow whenever someone creates a PR or push the code for the repo to run the automated test cases. Or you can trigger it as a cronjob like every monday at 9:15 AM you want to scan your code for vulnerabilities. 

- **Jobs and steps:** It contains the configuration and the steps to run in the job. For example to use a ubuntu machine to run the tasks and then checkout the code, set the environment variables and run some shell commands. You can divide the steps into different jobs based on requirements. There are a few reasons why one should have multiple jobs in a GitHub Actions workflow:
    - *To parallelize tasks*. Multiple jobs can run in parallel, which can speed up the overall workflow. For example, you could have one job run your unit tests and another job run your integration tests.
    - *To isolate tasks.* If one job fails, it won't affect the other jobs. This can be useful for preventing cascading failures. For example, you could have one job build your code and another job deploy your code. If the build job fails, the deployment job won't run.
    - *To use different environments.* You can use different jobs to run tasks on different machines or operating systems. For example, you could have one job run your tests on Linux and another job run your tests on Windows.
    - *To reuse jobs.* You can reuse jobs in different workflows. This can help you to keep your workflows DRY (don't repeat yourself).

- **Post Workflow:** After running all the required steps generally there are post workflow commands such as to upload the artifacts or test reports back to Github or cleaning up the build files.

Let’s create a workflow to see all these in action. 

\`\`\`python
import requests

print("Hello Github Actions!")

def quote():
    url = "https://api.quotable.io/random"

    response = requests.get(url)
    print(response.status_code)
    quote = response.json()
    return quote['content']

print(quote())
\`\`\`
 So here I have a basic python script which is using requests module to get a quote from this API. Lets try to run this locally and also i will save the output in a file called quote.txt

\`\`\`bash
python3 hello.py > quote.txt
\`\`\`

I have created a repository on github with the same file. Below is the requirements.txt file which contains the list of packages I need to run my application. For this one I only need the requests package. 

\`\`\`bash
requests==2.28.2
\`\`\`
I have opened this repository on GitHub.
Now go to Actions. Here you can see the some of the preconfigured workflows. Either you can choose any of these and customize them or create a new one yourself.

Let's name it \`workflow.yml\`. 

### Events

\`\`\`yaml
name: Quote Workflow
on: 
  workflow_dispatch:
  schedule:
  - cron: '30 3 * * 1-5'
\`\`\`

Name the workflow as "Quote Workflow".

Now I need to set when to run the workflow. If you check the documentation you will find there are lots of events defined. First i have added workflow_dispatch because I want to run the workflow manually. But let’s suppose I need my quote delivered at 9 AM daily. So I will use the schedule trigger and set the cron to "30 3 * * 1-5". So this will trigger the workflow on weekdays at 9AM local time or 3:30 AM UTC. Keep in mind that the cron always takes time in UTC so you need to adjust the cron according to your local time. Just like here I have reduced 5 hr 30 mins for IST time zone.

### Job
Now coming to the next part of the workflow, the jobs. 

\`\`\`yaml
jobs: 
  Run-python-script:
    runs-on: ubuntu-latest
\`\`\`

Here we will specify the name of the job('Run-python-script') to be run. 

Then we need to specify the machine OS on which we need to run this particular job. There are many options available but I am going with 'ubuntu-latest'.

#### Steps
After defining the machine we can start with the steps. First we will define the steps inside job to setup the required environment. 


1. We first need to checkout the code in the ubuntu machine. 
    \`\`\`bash
    steps: 
      - uses: actions/checkout@v3
    \`\`\` 
    
2. Then I will setup a particular version of python.
          
      \`\`\`yaml
          - uses: actions/setup-python@v3
              with:
              python-version: '3.10'
              architecture: 'x64'
      \`\`\`
3. Next I need to install my requirements from requirements.txt file. I will run the pip install command. Here i will name it as Installing the dependencies. 

    \`\`\`yaml
        - run: echo "Installing requirements.."
        - name: Install dependencies
            run: pip install -r requirements.txt 
    \`\`\`
4. Next I will try to run my script. So I will just write the shell command, that I have used locally and save the output of script in a txt file.

    \`\`\`yaml
            - run: echo "Triggering script."
            - name: Running Script
                run: python3 hello.py > artifact.txt 
    \`\`\`

  Remember, here we are not using "uses" because that is for predefined action. But here we will use "run" to run the shell command.  

  If you are facing difficulty then you can watch the video below:

![youtube](gGzUAu_ZTdM)

5. Now after this file('quote.txt') is created, to retrieve this file as artifact we will use the following step. I will set the parameters here as name and path of the artifact. Also i will set the retention period. This is the period for which the Github will retain or store the artifact created using this build. So I will set it for 1 day. If you set it to zero then the it will follow the retention policy of the repository which you can find in the Github settings. 

\`\`\`yaml
    - uses: actions/upload-artifact@v3
      with:
        name: quote
        path: ./artifact.txt
      retention-days: 1
\`\`\`
Now you can save the changes in your workflow file and then run the workflow from the Actions tab. 

Here is the complete workflow file for your reference.

\`\`\`yaml
name: Quote Workflow
on: 
  workflow_dispatch:
  schedule:
  - cron: '40 3 * * 1-5'
jobs: 
  Run-python-script:
    runs-on: ubuntu-latest
    steps: 
      - uses: actions/checkout@v3
      - uses: actions/setup-python@v3
        with:
          python-version: '3.10'
          architecture: 'x64'
      - run: echo "Installing requirements.."
      - name: Install dependencies
        run: pip install -r requirements.txt 
      - run: echo "Triggering script."
      - name: Running Script
        run: python3 hello.py > artifact.txt
      - uses: actions/upload-artifact@v3
        with:
          name: quote
          path: ./artifact.txt
            retention-days: 1 
\`\`\`

### Conclusion
GitHub Actions is a powerful tool that can help you to automate your workflows and improve your development process. By using multiple jobs in your workflows, you can parallelize tasks, isolate tasks, use different environments, and reuse jobs. This can help you to improve the performance, reliability, and maintainability of your workflows.

#### Important Links:
- [GitHub Actions Documentations](https://docs.github.com/en/actions)
- [GitHub Actions Marketplace](https://github.com/marketplace?type=actions)

`,
    date: "2023-12-26",
    category: "GitHub-Actions",
    featureImage: "/images/github-actions.png",
    featured: false,
    description: "This post contains the basics about Github Actions."
  },
  {
    id: 7,
    title: "In the Age of Microservices, Monitoring Made Easy with Prometheus",
    content: `
In today's world dominated by microservices, maintaining and monitoring each service can be a daunting task. Imagine visiting your webpage, only to find it unresponsive. You scramble to diagnose the issue, checking the front-end services first. You discover the backend isn't responding correctly, leading you to investigate backend service errors. Finally, you might uncover the root cause: the database is failing to respond to queries. This troubleshooting can be incredibly time-consuming. Thankfully, **Prometheus** is here to help.

## 💭 What is Prometheus? 
Prometheus is an open-source monitoring system designed to collect metrics from various sources, store them in a time series database, and expose them through a RESTful API. It's a popular choice for monitoring microservices and containerized applications.

Prometheus empowers you to:
- **Expose important metrics** from your resources.
- **Collect and store data** in a time series database.
- **Trigger alerts or notifications** based on predefined conditions.

Developed using Go and maintained by the Cloud Native Computing Foundation, Prometheus has become a cornerstone of modern monitoring practices.

## Unveiling the Prometheus Architecture
### Prometheus Server:
<img src="{{ site.baseurl }}/assets/images/prometheus-data-flow.png" style="float: right;">
The heart of the architecture, the Prometheus server, collects metrics and stores them in a time-series database. It can handle short-lived jobs through a push gateway, allowing them to push metrics directly instead of waiting to be scrapped.

- **Targets**: These are the services or machines you want to monitor, such as virtual machines in AWS or containerized services running on EC2 instances.

- **Client Libraries**: For easy monitoring integration, Prometheus offers client libraries that can be added to your application code.

- **Exporters**: When client libraries aren't feasible, exporters (like those for HAProxy or Linux systems) are used to expose Prometheus endpoints.

- **Configuration File**: This file defines the targets to be monitored. For dynamic targets, service discovery can be employed.

### Service Discovery
Service discovery in Prometheus is the process of automatically finding and registering the endpoints of targets for scraping. This is crucial because targets can be dynamically created or destroyed and might reside across various locations. For instance, in a Kubernetes cluster, pods from the same service can be spread across multiple nodes, and pods or nodes themselves can be dynamically created or destroyed.

<img src="{{ site.baseurl }}/assets/images/prometheus-service-discovery.png" style="float: right;">

Several service discovery mechanisms can be used with Prometheus, including:
- **Kubernetes Service Discovery:** Built-in to Kubernetes, this mechanism helps discover endpoints of Kubernetes services.
- **Consul Service Discovery:** CA popular service discovery system for finding endpoints of any service.
- **Etcd Service Discovery:** A distributed key-value store that can be used to store service endpoint information.
- **File-based Service Discovery:** A manual approach where you specify target endpoints in a file.

The specific steps involved in service discovery vary depending on the chosen mechanism. However, the general flow involves:

1. Target registration with the service discovery system.
2. Information storage about the target by the service discovery system.
3. Prometheus querying the service discovery system to locate target endpoints.
4. Prometheus scraping metrics from the targets.

### Time Series Database
After collection, metrics are stored in a time series database (TSDB). This specialized database efficiently stores and queries data as a series of measurements over time. Designed for scalability, it allows you to store vast amounts of data without compromising performance.

### PromQL
PromQL, short for Prometheus Query Language, is your key to unlocking the treasures hidden within your Prometheus monitoring data. It empowers you to craft powerful queries, navigate through mountains of metrics, and extract insights that illuminate the health and performance of your systems. PromQL expressions can be displayed as graphs, viewed as tabular data in Prometheus's expression browser, or consumed by external systems via the HTTP API https://prometheus.io/docs/prometheus/latest/querying/basics/.

Here are some PromQL examples:
\`\`\`bash
# Return all time series with the metric http_requests_total:
http_requests_total

# Return with the given label
http_requests_total{job="apiserver", handler="/api/comments"}

# Return a whole range of time for the same vector
http_requests_total{job="apiserver", handler="/api/comments"}[5m]

# Use regular expression to select query selective data
http_requests_total{job=~".*server"}

#Return the 5-minute rate of the http_requests_total metric for the past 30 minutes, with a resolution of 1 minute.
rate(http_requests_total[5m])[30m:1m]
\`\`\`

### AlertManager
Alertmanager, a Prometheus plugin, routes alerts to various destinations like email, Slack, or PagerDuty. It excels at deduplicating, grouping, and routing alerts to the appropriate receivers. Additionally, it allows for silencing and inhibiting alerts based on specific criteria.

Key functionalities of Alertmanager include:
- **Deduplication:** Prevents alert spamming by sending only one notification for a recurring issue.
- **Grouping:** Groups related alerts together based on shared labels, providing a more concise view of potential problems.
- **Routing:** Directs alerts to the designated receiver integrations based on predefined configurations.
- **Silencing:** Allows you to temporarily suppress alerts that are not critical or that you are already aware of.
- **Inhibition:** Prevents alerts from being triggered if they are caused by other higher-priority alerts.

Prometheus utilizes a rule-based alerting system. You define rules that specify when an alert should be sent. To create an Alertmanager rule, you'll need to configure the following:
- \`Metric:\` The metric that you want to monitor.
- \`Operator:\` The comparison operator that you want to use.
- \`Threshold:\` The value that the metric must cross in order to trigger an alert.
- \`Duration:\` The amount of time that the metric must exceed the threshold in order to trigger an alert.
Once a rule is defined, Prometheus continuously monitors the metric. If the metric breaches the threshold for the specified duration, an alert will be sent through Alertmanager to the designated notification channels.
Example of a Prometheus alert rule:

\`\`\`bash
alert: HighCPUUsage
expr: cpu_usage_percent{job="web"} > 90
for: 5m
labels
  severity: critical
annotations:
  summary: CPU usage for the web job is above 90%.
  description: CPU usage for the web job has been above 90% for the past 5 minutes.
\`\`\`

This rule triggers a notification if CPU usage for the "web" job remains above 90% for 5 minutes. Additionally, the alert will be labeled with a "critical" severity to ensure it receives the necessary attention.

I have tried to explain the same using the following video. If you have any questions you can comment on the YouTube video.

![youtube](TS6MaeK1w9w)

## Conclusion: 
Prometheus offers a powerful and versatile monitoring solution, empowering you to gain deep insights into the health and performance of your microservices and containerized applications. By leveraging its functionalities like service discovery, time-series data storage with PromQL, and alert management with Alertmanager, you can proactively identify and address potential issues before they significantly impact your systems.
#### Important links
- [Getting Started with Prometheus](https://prometheus.io/docs/prometheus/latest/getting_started/)
- [Prometheus Documentation](https://prometheus.io/docs/introduction/overview/)
- [Alertmanager Documentation](https://prometheus.io/docs/alerting/latest/alertmanager/)

`,
    date: "2024-02-25",
    category: "Monitoring",
    featureImage: "/images/prometheus.png",
    featured: false,
    description: "This post contains the basics of Prometheus and web app monitoring."
  },
  {
    id: 8,
    title: "Getting started with Kubernetes",
    content: `
Imagine having a cluster of containers, each housing a vital component of your application. But how do you manage them all? How do you ensure they communicate effectively, scale effortlessly, and recover from failures autonomously? That's where Kubernetes comes to the rescue, with its powerful tools and intelligent orchestration capabilities.

## What is Kubernetes?
Kubernetes (often abbreviated as k8s) is an open-source container orchestration platform that automates the deployment, scaling, and management of containerized applications. It excels at managing hundreds of containerized applications, simplifying the complexities associated with large-scale deployments.

Previously, monolithic applications were broken down into smaller microservices and then containerized. However, managing these individual containers across host machines with network configurations, volumes, and scaling became a significant challenge. Scripts and automation tools often reached their limits, creating a need for an orchestration solution.

Kubernetes addresses these challenges by providing functionalities for:

- High Availability: Ensuring continuous operation despite node failures.
- Scalability: Effortlessly scaling applications up or down based on demand.
- Disaster Recovery: Recovering from outages and restoring the cluster to a healthy state.

By automating many aspects of application management, Kubernetes empowers developers and administrators to focus on building and scaling their applications rather than being bogged down by infrastructure intricacies.

## Demystifying the Kubernetes Architecture
    
Kubernetes operates on a master-slave architecture. It consists of a control plane, which acts as the brain of the operation, and worker nodes, which are the workhorses responsible for running the actual containers.
    
- **Worker Nodes (Minions/Machines):** These are the servers where containerized applications reside. Each worker node runs essential Kubernetes components like:
  - Kubelet: Communicates with the control plane to receive instructions.
  - Kube-proxy: Manages network routing within the cluster.
  - Container Runtime: The engine for running containers (e.g., Docker, containerd).

  Worker nodes receive instructions from the master, dynamically adjusting the number of running containers based on workload demands. They can also be scaled up or down as needed.

- **Master Node:** This is the central hub where the control plane resides. The control plane consists of several key components:
  - **API Server:** The entry point for interacting with the cluster. Clients like UI tools, automation scripts, and the kubectl CLI communicate with the API server for cluster management.
  - **Controller Manager:** Continuously monitors the cluster state, ensuring everything runs smoothly and taking corrective actions when necessary (e.g., restarting containers).
  - **Scheduler:** Makes informed decisions about where to place pods (groups of containers) on worker nodes. It considers resource availability on worker nodes and resource requirements of pods.
  - **Etcd:** Acts as the distributed key-value store, holding the current state of the cluster (node and container status). This data is crucial for cluster restoration using etcd snapshots.
    
> For enhanced fault tolerance, you can deploy multiple master nodes. This redundancy ensures the ability to recover the cluster to its previous state in case of a master node failure.
    
## Core Concepts: Building Blocks of Kubernetes
Kubernetes achieves container orchestration through a set of powerful features and concepts. Here's a glimpse into some of the fundamental building blocks:

- **Pods:** The basic units of deployment in Kubernetes. A pod encapsulates one or more containers and their associated resources (storage, networking). Multiple pods can reside on a single worker node. The scheduler in the master node allocates pods to worker nodes based on resource availability and pod requirements. Unlike pods, which have dynamic IP addresses that change with creation and deletion, we have...
- **Services:** These define stable network endpoints that provide access to a set of pods. Services enable features like load balancing and service discovery within the cluster.
- **Deployments:**  These represent a declarative approach to defining the desired state of a pod. You specify the configuration, and Kubernetes works towards achieving that state. For instance, you might define the number of pod replicas you desire, and the deployment controller ensures that many pods are always running.

- **ConfigMaps:**  These are lightweight key-value data stores that provide a mechanism to decouple application configuration data from container images. ConfigMaps are ideal for storing non-sensitive configuration data like environment variables, configuration files, or API keys that all containers within a pod need to access. They are mounted into the container filesystem at runtime, making the data readily available to the application. Unlike Secrets, ConfigMaps are stored in plain text within etcd.

- **Secrets:**  These are similar to ConfigMaps but are designed to securely store sensitive information like passwords, authentication tokens, or certificates. Secrets are base64 encoded at rest and during transport, ensuring confidentiality. They are also mounted into container filesystems, but the data is retrieved from the Kubernetes API server, which uses a secure mechanism to access the secrets store.

**Creating Resources:**  You interact with the Kubernetes cluster through the \`API server\` on the master node. You can use the Kubernetes dashboard UI, API calls using scripts, or the kubectl command-line tool (CLI) to provide instructions in YAML or JSON format for creating pods, deployments, and other resources within the cluster.

I have tried to explain the same using the following video. If you have any questions you can comment on the YouTube video.

![youtube](l_ZUDOGPB6U)

## Conclusion:
Kubernetes is a powerful tool that simplifies the management of complex containerized applications. By leveraging its features like self-healing capabilities, automatic scaling, and service discovery, you can build and deploy robust, resilient containerized applications.

#### Important links
- [Getting Started with Kubernetes](https://kubernetes.io/docs/setup/)
- [Kubernetes Documentation](https://kubernetes.io/docs/home/)

`,
    date: "2024-03-22",
    category: "Kubernetes",
    featureImage: "/images/kubernetes.png",
    featured: false,
    description: "This post contains the basics of Kubernetes."
  },
  {
    id: 9,
    title: "Understanding Kubernetes Networking",
    content: `
Kubernetes applications are built on containers, which are small, lightweight units of software. But how do these containers communicate with each other? That's where Kubernetes networking comes in. Kubernetes networking is the underlying infrastructure that enables communication between containers and pods (a group of containers) within the cluster and outside of it.

A well-configured Kubernetes networking setup is essential for ensuring several key aspects of your containerized applications:

- High Availability: Ensures your application remains operational even if individual components fail.
- Scalability: Allows you to easily add or remove resources as your application's needs change.
- Security: Protects your application from unauthorized access.

## Kubernetes Networking Fundamentals

To understand Kubernetes networking you must know the basic k8s resources like Pods, Service and Endpoints.

- **Pods:** The basic unit of deployment in Kubernetes. A pod contains one or more containers that share the same storage and network namespace.
- **Services:** Act as an abstraction layer for a group of pods. They provide a single entry point for applications to access these pods, regardless of their IP addresses or locations within the cluster.
- **Endpoints:** Represent the actual network addresses of pods that can be reached by a service. As pods are created or destroyed, the endpoints list is dynamically updated.

These resources come together to create various communication channels within a Kubernetes cluster.

## Communication Scenarios in Kubernetes Networking

- **Container-to-Container (Intra-Pod):**
  - **Mechanism:** Pods share a single network namespace. This allows containers within the same pod to communicate directly with each other using \`localhost\` or container IP addresses.
  - **Use Case:** Ideal for tightly coupled containers that need to communicate frequently and share resources efficiently.

- **Pod-to-Pod Networking:**
  - **Mechanism:** Kubernetes assigns a unique Pod IP address to each pod. Pods can discover each other's IP addresses through service discovery mechanisms like DNS or hostname resolution.
  - **Use Case:** Essential for communication between loosely coupled microservices or applications within the cluster that are not necessarily co-located on the same node.

- **Pod-to-Service Networking:**
  - **Mechanism:** Services act as a logical abstraction for pods. When you create a service, you define a selector that matches pods based on labels. The service then routes incoming traffic to the healthy pods that match the selector. The \`targetPort\` key in the service definition specifies which port on the pod the traffic should be directed to.
  - **Use Case:** Services enable pods to communicate with each other even when deployed across different nodes or namespaces within the cluster. This simplifies service discovery and makes deployments more scalable.

- **External-to-Service Networking:**
  - **Mechanism:** There are various ways to expose services to the external network, depending on the type of service you choose and the level of access you want to provide. We'll explore these service types in the next section.
  - **Use Case:** Used to allow external users or other services outside of your cluster to communicate with your application.

## Types of K8s Services

The type of service you choose determines how your service is exposed to the external network:

### ClusterIP
A service type that makes a service accessible only within the Kubernetes cluster. This is the default service and you don’t need to mention it during creation.

### NodePort
This service type exposes a service on a specific port on each node in the cluster. External clients can access the service by connecting to the node's IP address and the exposed port. While NodePort services provide external access, it's important to note that they expose cluster nodes directly, which can be a security concern.

### LoadBalancer
This service type uses a cloud provider's load balancer to distribute traffic across multiple pods in your service. When you create a LoadBalancer service, Kubernetes automatically creates a NodePort and a ClusterIP service in the background. LoadBalancer services are generally preferred over NodePort services for production deployments due to improved security and scalability.

![youtube](IInUXNhG4z8)

## Conclusion

Kubernetes networking provides a robust and flexible framework for containerized applications. By understanding the different communication scenarios and service types, you can effectively configure your cluster to achieve high availability, scalability, and security for your applications. Whether you're dealing with tightly coupled containers within a pod or loosely coupled microservices spread across the cluster, Kubernetes networking offers the tools you need to ensure seamless communication and efficient resource utilization.

#### Important links
- [Cluster Networking](https://kubernetes.io/docs/concepts/cluster-administration/networking/)
- [Kubernetes Documentation](https://kubernetes.io/docs/home/)
`,
    date: "2024-04-23",
    category: "Kubernetes",
    featureImage: "/images/k8s-networking.png",
    featured: false,
    description: "This post contains the networking of Kubernetes, covering service types and communication scenarios."
  },
  {
    id: 10,
    title: "Best Practices for Kubernetes Networking",
    content: `
Kubernetes networking provides a robust and flexible framework for containerized applications. By understanding the different communication scenarios and service types, you can effectively configure your cluster to achieve high availability, scalability, and security for your applications. Whether you're dealing with tightly coupled containers within a pod or loosely coupled microservices spread across the cluster, Kubernetes networking offers the tools you need to ensure seamless communication and efficient resource utilization.

Let's check out some of the best practices for Kubernetes networking

- **Use meaningful names**: Use relevant names when creating a Kubernetes service. You might say this is a simple advice and might not think about it when defining one or two service for your application. But when you have multiple services connecting through multiple pods and each pod consists of different containers then it will be tough to manage. So following a proper naming convention helps you in segregating your work and you can deploy or test without confusion.

- **Selector Definition**: Use labels to target specific pods for each service. Ensure your selectors accurately match the pods you intend to expose.

- **Selecting Right Service Types**: Identifying the right service type based on your requirements is important. For example,
    - 'ClusterIP Service' is only accessible inside the cluster so it can be used only for backend services.
    - 'NodePort Service' will expose worker node ports to public access so this one has limitations and it's okay to use it for simple application but not for production.
    - 'LoadBalancer Service' utilizes the load balancer by the cloud provider and provide safe access of the app running in the cluster. 

- **Port Consistency**: You should maintain port consistency between service's external port and target port. First this will help you in remembering less number of ports and second it reduces the port mapping confusion when you are dealing with multiple services.

- **Service Discovery:** You should leverage service discovery mechanisms like DNS to allow pods to discover and communicate with services using their names.

- **Use Network Policies:** Limit access to services using Network Policies to control the communication between pods and services as needed.

- **Health Checks:** Configure health checks for pods by mentioning readiness and liveness probe, especially with NodePort or LoadBalancer, to ensure services routes the traffic to healthy pods.

You can watch the video for more information:

![youtube](IInUXNhG4z8)

## Conclusion:
By following these best practices, you can establish a solid foundation for your Kubernetes networking. Clear service names, well-defined selectors, and appropriate service types ensure efficient communication within your cluster. Consistent ports, service discovery mechanisms, and network policies further enhance manageability and security. Remember, a well-configured Kubernetes network empowers your containerized applications to thrive, promoting high availability, scalability, and robust communication.

#### Important links
- [Cluster Networking](https://kubernetes.io/docs/concepts/cluster-administration/networking/)
- [Kubernetes Documentation](https://kubernetes.io/docs/home/)
- [Docker Networking](https://www.mayanksonidev.com/docker/2023/10/12/docker-networking.html)
    `,
    date: "2024-04-30",
    category: "Kubernetes",
    featureImage: "/images/k8s-networking-best.jpg",
    featured: false,
    description: "This post contains the best practices for setting up networking of Kubernetes."
  },
  {
    id: 11,
    title: "Keeping Your Code Healthy: Dependency Management with Dependabot",
    content: `
In the fast-paced world of software development, keeping your codebase healthy and secure is paramount. One crucial aspect of achieving this is effective dependency management. This blog post dives into the importance of managing dependencies, explores traditional approaches, introduces Dependabot as a powerful tool, and guides you through its setup using GitHub Actions.

## Importance of Dependency Management
Imagine a complex recipe with various ingredients. Just like missing or outdated ingredients can ruin a dish, poorly managed dependencies can wreak havoc on your code. Dependencies are external libraries or modules your project relies on to function. Outdated dependencies can introduce security vulnerabilities, compatibility issues, and unexpected bugs.

Effective dependency management ensures you're using the most compatible and secure versions of your dependencies. This translates to a more reliable, secure, and maintainable codebase in the long run.

## The Manual Struggle: Traditional Dependency Management

Traditionally, developers often manually check for dependency updates. This process can be cumbersome and time-consuming, especially for large projects with numerous dependencies. It involves:

- Regularly reviewing documentation or changelogs of your dependencies.
- Manually searching for new releases and security advisories.
- Manually updating versions in your project files (e.g., package.json or requirements.txt).
- Testing the application thoroughly after each update to ensure compatibility.

This manual approach is error-prone and can be a significant bottleneck in the development workflow.

## Introducing Dependabot: Your Automated Dependency Guardian

Dependabot is a lifesaver for developers struggling with manual dependency management. It's a service that automatically scans your codebase for outdated dependencies and potential security vulnerabilities. It then creates pull requests on your GitHub repository suggesting updates to the vulnerable or outdated dependencies.

Here's how Dependabot works:

1. **Integration:** You configure Dependabot to work with your GitHub repository.
2. **Scanning:** Dependabot regularly scans your codebase for dependencies and their versions.
3. **Vulnerability Detection:** It checks for known security vulnerabilities in the identified dependencies.
4. **Pull Request Creation:** If vulnerabilities or outdated versions are found, Dependabot creates pull requests suggesting updates.
5. **Review and Merge:** You review the pull requests, test the updated dependencies, and merge them if everything looks good.

This automated approach significantly reduces the time and effort required for dependency management, allowing developers to focus on core development tasks.

## A Real-World Example: The Log4j Vulnerability

The importance of automated dependency management was highlighted in late 2021 with the discovery of a critical vulnerability in the widely used logging library Log4j (versions 2.0-beta9 to 2.14.1). This vulnerability, nicknamed "Log4Shell," allowed attackers to remotely execute malicious code on affected systems. The ease of exploitation and widespread use of Log4j made this a significant security event.

Dependabot played a crucial role in mitigating the Log4j vulnerability. Many developers who had integrated Dependabot received automated pull requests suggesting updates to their Log4j dependencies, helping them patch their applications quickly and effectively. This demonstrates the value of automated dependency management in protecting your codebase from known vulnerabilities.

Do checkout the video for more information. Please subscribe to the channel if you find the content useful.

![youtube](8ZUyg1D9pSs)

## Conclusion:
With the ever-increasing complexity of modern software, automated dependency management is no longer a luxury; it's a necessity. By integrating Dependabot and GitHub Actions, you can significantly improve the security and maintainability of your codebase, allowing you to build more robust and reliable applications.

#### Important links
- [Dependabot Quickstart Guide](https://docs.github.com/en/code-security/getting-started/dependabot-quickstart-guide)
- [Dependabot alerts](https://docs.github.com/en/code-security/dependabot/dependabot-alerts/about-dependabot-alerts)

`,
    date: "2024-05-05",
    category: "GitHub-Actions",
    featureImage: "/images/dependabot.jpg",
    featured: false,
    description: "This post show how to use Dependabot for dependency management in GitHub Actions for your project."
  },
  {
    id: 12,
    title: "Reusable Workflow and Composite Action",
    content: `
If you are working with continuous integration and continuous deployment, you often have to repeat the same steps in your workflows. For example, for your deployment workflow in Azure, you need to set up Azure CLI, configure the environment and other credentials, and run the actual deployment process. Additionally, you need to clean up afterward.

To streamline this process, you can use \`workflow templates\` and \`composite actions\` in GitHub Actions. Let's see what these are and how you can leverage them in your workflows.

## What is a workflow template?
A workflow template is a pre-configured workflow that you can use as a starting point for your own workflows. Templates save you time and effort by providing a basic structure that you can customize to fit your needs. They are defined in YAML files and stored in a separate repository. This means you can share them with others or reuse them in different projects.

Here are some benefits of using workflow templates in GitHub Actions:

- **Save time**: Workflow templates provide a basic structure that you can tailor to your needs, saving you significant time.
- **Increase consistency**: Using templates ensures your workflows are consistent and follow best practices.
- **Improve collaboration**: Workflow templates make it easier to collaborate on workflows with others.
- **Reduce errors**: Templates help reduce errors by providing well-tested and documented workflows.

## Example of Workflow Template

### Main workflow:
\`\`\`bash
name: My workflow
on: [push]

jobs:
  main:
    uses: mayanksoni/github-actions/.github/workflows/template.yml
    with:
      environment: "dev"
      dockerfile: "Dockerfile.server"
      tag: "latest.dev"
\`\`\`

This main workflow calls a template file stored in the repository \`mayanksoni/github-actions\`, specifically at the path \`.github/workflows/template.yml\`.

### Template Workflow:
\`\`\`yaml
name: Docker Build
on:
  workflow_call:
    inputs:
      environment:
        required: true
        type: string
      dockerfile:
        required: true
        type: string
      tag:
        required: true
        type: string

env:
  ENVIRONMENT: \${{ github.workspace }}/\${{ inputs.environment }}

jobs:
  main:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v2
      - name: Build image
        run: echo "New build triggered"
\`\`\`

This template workflow receives inputs from the main workflow. To use these inputs, you can reference them as shown in the \`ENVIRONMENT\` variable.

In this way, you have a reusable template that builds your image based on different inputs. You can store this template file in the same repository as the main workflow or in a different repository.

## What is composite action?

A composite action in GitHub Actions is a single action that runs multiple other actions. This is useful for grouping related tasks or creating more complex workflows. Composite actions are defined in YAML files and stored in the same repository as the workflow using them, meaning they are not reusable in other repositories but can simplify and modularize your workflows.

Composite actions can have inputs and outputs and support nesting up to 10 layers, allowing you to call other composite actions within them.

## Example of Composite Action

### Workflow Using Composite Action:
\`\`\`yaml
name: My workflow
on: [push]

jobs:
  main:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v2
      - id: docker_build
        uses: ./.github/actions/docker-build
        with:
          dockerfile: Dockerfile
          tag: latest
          environment: production
      - name: Use the output
        run: echo "The image name is \${{ steps.docker_build.outputs.image_name }}"
\`\`\`

Here, \`./.github/actions/docker-build\` denotes the folder containing the \`action.yml\` file for the composite action. Unlike workflow templates, you specify the folder name in \`uses\`.

### Composite Action (action.yml):
\`\`\`yaml
name: Docker Build
description: "Docker build"

inputs:
  dockerfile:
    description: "Path of the Docker file"
    required: true
  tag:
    description: "Tag to be assigned"
    required: true
  environment:
    description: "Environment Type"
    required: true

outputs:
  image_name:
    description: "The name of the Docker image"

runs:
  using: "composite"
  steps:
    - name: Trigger build
      run: |
        echo "New build triggered for \${{ inputs.environment }} environment"
        echo "::set-output name=image_name::myapp:\${{ inputs.tag }}"
      shell: bash
\`\`\`

In the composite action file, we define the inputs that the main workflow will provide and the outputs that the composite action will return. The output variable first needs to be defined and then assigned a value.

Feel free to watch our video for more detailed information. If you find the content useful, please subscribe to the channel.

![youtube](bjFks2s6IHI)

## Conclusion:
These powerful features allow you to streamline your continuous integration and continuous deployment processes, saving time and effort while ensuring consistency and reducing errors.

By using workflow templates, you can create a reusable structure that can be customized for different projects, facilitating collaboration and maintaining best practices. Composite actions, on the other hand, enable you to modularize your workflows by grouping related tasks together, making your CI/CD pipeline more efficient and manageable.

Both workflow templates and composite actions are essential tools for anyone looking to optimize their GitHub Actions workflows. We hope this blog post has provided you with valuable insights and practical examples to help you get started with these features.

#### Important links
- [GitHub Actions Documentation](https://docs.github.com/en/actions)
- [Reusing Workflows](https://docs.github.com/en/actions/using-workflows/reusing-workflows)
- [Composite Actions](https://docs.github.com/en/actions/creating-actions/creating-a-composite-action)
  `,
    date: "2024-05-25",
    category: "GitHub-Actions",
    featureImage: "/images/reusable-gh-action.jpg",
    featured: false,
    description: "This post show how to use Dependabot for dependency management in GitHub Actions for your project."
  },
  {
    id: 13,
    title: "Serverless Application on AWS",
    content: `
Serverless computing is a great way for  application development. It eliminates the need to manage servers. The cloud providers even take care of scalability, monitoring, logging etc. This means as a developer you can completely focus on app development without worrying about the infrastructure and performance. And you will only be paying for the net usage of resources.

Let's try to create a serverless application using AWS lambda, Dynamodb and API Gateway.

## Components of application
Let's try to create a serverless application using AWS lambda, Dynamodb and API Gateway.

- **API Gateway**: API gateway is the entrypoint of our serverless application. It will handle the incoming requests from the clients and gave the response from the backend service. We will use API Gateways to create Rest APIs. API Gateway can handle the task like authentication, authorization and traffic management and monitoring.
- **Lambda funtion**: Lambda function is small self contained units of code that run in response of events. They can be written in multiple languages like Python, Nodejs, Java and more. When an API Gateway receives a request, it can be configured to trigger a specific Lambda function. This function then processes the request, performs the necessary logic, and returns a response. The best thing about Lambda is that you only pay for the compute time used, making it highly cost-effective.
- **DynamoDB**: DynamoDB is a fully managed NoSQL database service that offers fast and predictable performance with seamless scaling. We'll use DynamoDB to store the data that our API will manage. We'll create tables, define data schemas, and perform CRUD (Create, Read, Update, Delete) operations using DynamoDB's powerful API.

## Lab

**Step 1**: Create IAM role
1. Go to IAM from your AWS console.
2. Choose the "Roles" option from the left sidebar.
3. Create a role with the following properties.
- Trusted entity - Lambda
- Role name - lambda-apigateway-role
- Permissions - To allow lambda to read and write dynamodb and to write the cloud watch logs.
\`\`\`bash
{
	"Version": "2012-10-17",
	"Statement": [
    {
      "Sid": "Statement1",
      "Action": [
        "dynamodb:DeleteItem",
        "dynamodb:GetItem",
        "dynamodb:PutItem",
        "dynamodb:Query",
        "dynamodb:Scan",
        "dynamodb:UpdateItem"
      ],
      "Effect": "Allow",
      "Resource": "*"
    },
    {
      "Sid": "Statement2",
      "Resource": "*",
      "Action": [
        "logs:CreateLogGroup",
        "logs:CreateLogStream",
        "logs:PutLogEvents"
      ],
      "Effect": "Allow"
    }
	]
}
\`\`\`

**Step 2**: Create Lambda function
1. Click \`Create function\` in AWS Lambda Console
2. Select \`Author from scratch\`. Use name MyLambdaFunction , select Python 3.12 as Runtime. Under Permissions, select \`Use an existing role\`, and select lambda-apigateway-role that we created, from the drop down.
3. Click \`Create function\`
4. Replace the boilerplate coding with the following code snippet. Click on Deploy.
\`\`\`bash
from __future__ import print_function

import boto3
import json

print('Loading function')


def lambda_handler(event, context):
    '''Provide an event that contains the following keys:

      - operation: one of the operations in the operations dict below
      - tableName: required for operations that interact with DynamoDB
      - payload: a parameter to pass to the operation being performed
    '''
    #print("Received event: " + json.dumps(event, indent=2))

    operation = event['operation']

    if 'tableName' in event:
        dynamo = boto3.resource('dynamodb').Table(event['tableName'])

    operations = {
        'create': lambda x: dynamo.put_item(**x),
        'read': lambda x: dynamo.get_item(**x),
        'update': lambda x: dynamo.update_item(**x),
        'delete': lambda x: dynamo.delete_item(**x),
        'list': lambda x: dynamo.scan(**x),
        'echo': lambda x: x,
        'ping': lambda x: 'pong'
    }

    if operation in operations:
        return operations[operation](event.get('payload'))
    else:
        raise ValueError('Unrecognized operation "{}"'.format(operation))
\`\`\`
5. To test the function you can create a new Test event with this payload.
\`\`\`json
{
    "operation": "echo",
    "payload": {
        "somekey1": "somevalue1",
        "somekey2": "somevalue2"
    }
}
\`\`\`
Click on "Test".

Sample Output:
\`\`\`bash
{
  "somekey1": "somevalue1",
  "somekey2": "somevalue2"
}
\`\`\`


**Step 3**: Create DynamoDB
1. Open the DynamoDB console.
2. Choose Create table.
3. Create a table with the following settings.
    - Table name – lambda-apigateway
    - Primary key – id (string)
4. Choose Create.

**Step 4**: Create API Gateway
1. Go to API Gateway console
2. Scroll down and select \`Build\` for REST API
3. Give the API name as \`Dynamo\`, keep everything as is, click \`Create API\`
4. By default, you will get the root path. But we will create one endpoint path.
6. Input \`dynamo\` in the Resource Name, Resource Path will get populated. Click \`Create Resource\`
7. Let's create a POST Method for our API. With the \`/dynamo\` resource selected, Click \`Actions\` again and click \`Create Method\`.
8. Select \`POST\` from drop down , then click checkmark
9. Select \`Lambda Function\` option for integration. Choose the lambda function that we have created earlier. Click \`Save\`.
> You should get the request-response workflow in the API Gateway page. 
10. Deploy the API
    1. Click on \`Deploy API\` button. 
    2. Select \`New Stage\`
    3. Give \`prod\` as Stage Name.
Now to invoke the API, go to the "Stages" from the left sidebar and get the endpoint for /dynamo path.

**Step 5**: Test the application
- In Postman, you can use the URL from the API gateway and this payload to create some data in the dynamo db.
\`\`\`bash
{
    "operation": "create",
    "tableName": "lambda-apigateway",
    "payload": {
        "Item": {
            "id": "1",
            "number": 12
        }
    }
}
\`\`\`
- To get the stored items list, you can use this payload to get the list of items stored in the database.
\`\`\`bash
{
    "operation": "list",
    "tableName": "lambda-apigateway",
    "payload": {}
}
\`\`\`

If these URLS gave you successfull response, then it means you application is working properly. 

Congratulations on creating a serverless application from scratch. Now it's your opportunity to modify the configuration and lambda function and expand your knowledge with serverless applications.

Feel free to watch our video for more detailed information. If you find the content useful, please subscribe to the channel.

{% youtube "https://youtu.be/xyHN2jQvJfM" %}

## Bonus
Here is the cloud formation template that can perform all the above steps at once.

\`\`\`bash
AWSTemplateFormatVersion: '2010-09-09'
Description: Serverless application

Resources:
  # Create IAM Role for Lambda
  LambdaAPIGatewayRole:
    Type: 'AWS::IAM::Role'
    Properties: 
      AssumeRolePolicyDocument:
        Version: '2012-10-17'
        Statement: 
          - Effect: 'Allow'
            Principal: 
              Service: 
                - 'lambda.amazonaws.com'
            Action: 
              - 'sts:AssumeRole'
      Policies:
        - PolicyName: 'LambdaDynamoDBPolicy'
          PolicyDocument:
            Version: '2012-10-17'
            Statement:
              - Effect: 'Allow'
                Action: 
                  - 'logs:CreateLogGroup'
                  - 'logs:CreateLogStream'
                  - 'logs:PutLogEvents'
                Resource: 'arn:aws:logs:*:*:*'
              - Effect: 'Allow'
                Action: 
                  - 'dynamodb:*'
                  - 'dynamodb:DeleteItem'
                  - 'dynamodb:GetItem'
                  - 'dynamodb:PutItem'
                  - 'dynamodb:Query'
                  - 'dynamodb:Scan'
                  - 'dynamodb:UpdateItem'
                Resource: '*'
                
  # Create DynamoDB Table
  DynamoDBTable:
    Type: 'AWS::DynamoDB::Table'
    Properties:
      TableName: 'lambda-apigateway'
      AttributeDefinitions:
        - AttributeName: 'id'
          AttributeType: 'S'
      KeySchema:
        - AttributeName: 'id'
          KeyType: 'HASH'
      ProvisionedThroughput:
        ReadCapacityUnits: 5
        WriteCapacityUnits: 5

  # Create Lambda Function
  MyLambdaFunction:
    Type: 'AWS::Lambda::Function'
    Properties: 
      FunctionName: 'MyLambdaFunction'
      Handler: 'index.lambda_handler'
      Runtime: 'python3.12'
      Role: !GetAtt LambdaAPIGatewayRole.Arn
      Code:
        ZipFile: |
          from __future__ import print_function

          import boto3
          import json

          print('Loading function')


          def lambda_handler(event, context):
              '''Provide an event that contains the following keys:

                - operation: one of the operations in the operations dict below
                - tableName: required for operations that interact with DynamoDB
                - payload: a parameter to pass to the operation being performed
              '''
              #print("Received event: " + json.dumps(event, indent=2))

              operation = event['operation']

              if 'tableName' in event:
                  dynamo = boto3.resource('dynamodb').Table(event['tableName'])

              operations = {
                  'create': lambda x: dynamo.put_item(**x),
                  'read': lambda x: dynamo.get_item(**x),
                  'update': lambda x: dynamo.update_item(**x),
                  'delete': lambda x: dynamo.delete_item(**x),
                  'list': lambda x: dynamo.scan(**x),
                  'echo': lambda x: x,
                  'ping': lambda x: 'pong'
              }

              if operation in operations:
                  return operations[operation](event.get('payload'))
              else:
                  raise ValueError('Unrecognized operation "{}"'.format(operation))

  # Create API Gateway
  MyApiGateway:
    Type: 'AWS::ApiGateway::RestApi'
    Properties:
      Name: 'DynamoDBOperations'

  # Create API Gateway Resource
  ApiResource:
    Type: 'AWS::ApiGateway::Resource'
    Properties:
      RestApiId: !Ref MyApiGateway
      ParentId: !GetAtt MyApiGateway.RootResourceId
      PathPart: 'dynamodbmanager'

  # Create API Gateway Method
  ApiMethod:
    Type: 'AWS::ApiGateway::Method'
    Properties:
      MethodResponses: 
        - ResponseModels: 
            application/json: Empty
          StatusCode: 200
      ResourceId: !Ref ApiResource
      RestApiId: !Ref MyApiGateway
      HttpMethod: 'POST'
      AuthorizationType: 'NONE'
      Integration:
        Type: 'AWS'
        IntegrationHttpMethod: 'POST'
        Uri: 
          Fn::Sub: arn:aws:apigateway:\${AWS::Region}:lambda:path/2015-03-31/functions/\${MyLambdaFunction.Arn}/invocations
        IntegrationResponses: 
          - StatusCode: 200


  # Deployment of API Gateway
  ApiDeployment:
    Type: 'AWS::ApiGateway::Deployment'
    DependsOn: ApiMethod
    Properties:
      RestApiId: !Ref MyApiGateway
      StageName: 'prod'

  # Permission for API Gateway to invoke Lambda
  LambdaApiGatewayInvokePermission:
    Type: 'AWS::Lambda::Permission'
    Properties: 
      FunctionName: !GetAtt MyLambdaFunction.Arn
      Action: 'lambda:InvokeFunction'
      Principal: 'apigateway.amazonaws.com'
      SourceArn: !Sub 'arn:aws:execute-api:\${AWS::Region}:\${AWS::AccountId}:\${MyApiGateway}/*/*/dynamodbmanager'

Outputs:
  ApiUrl:
    Description: "URL for the API endpoint"
    Value: 
      Fn::Sub: 
        - https://\${MyApiGateway}.execute-api.\${AWS::Region}.amazonaws.com/prod/dynamodbmanager
        - MyApiGateway: !Ref MyApiGateway
\`\`\`

## Conclusion:
By leveraging serverless architecture, we eliminated the complexities of managing infrastructure, allowing us to focus solely on application logic. The pay-per-use model of AWS Lambda ensures cost-efficiency, making it an ideal choice for various applications.

Through hands-on experience, we gained insights into the power of combining these services to create a seamless and responsive application. Whether you're a seasoned developer or just starting, understanding serverless computing and these AWS services is essential for building modern, cloud-native applications.

We encourage you to experiment further with AWS Lambda, DynamoDB, and API Gateway to unlock the full potential of serverless computing.

#### Important links
- [Serverless on AWS](https://aws.amazon.com/serverless/)
- [API Gateway](https://docs.aws.amazon.com/apigateway/latest/developerguide/welcome.html)
- [AWS Lambda](https://docs.aws.amazon.com/lambda/latest/dg/welcome.html)
- [AWS DynamoDB](https://docs.aws.amazon.com/amazondynamodb/latest/developerguide/Introduction.html)

`,
    date: "2024-08-08",
    category: "AWS",
    featureImage: "/images/serverless-aws.jpeg",
    featured: false,
    description: "This post show how to create serverless application in AWS using AWS lambda, Dynamodb and API Gateway."
  }
];

export default posts;